import React, { useState } from 'react';
import { BaseCRUDPage } from '../common';
import { Input, Textarea, RequiredIndicator, Column, FieldError } from '../../components';
import { companyService } from '../../services';
import { Company, CompanyCreate, CompanyUpdate } from '../../types';

interface CompanyFormData {
  code: string;
  name: string;
  description: string;
  address: string;
}

const CompaniesPage: React.FC = () => {
  const [formData, setFormData] = useState<CompanyFormData>({
    code: '',
    name: '',
    description: '',
    address: ''
  });

  // Define table columns
  const columns: Column[] = [
    { key: 'code', label: 'Code', sortable: true, searchable: true },
    { key: 'name', label: 'Name', sortable: true, searchable: true },
    { key: 'description', label: 'Description', sortable: true, searchable: true },
    { key: 'address', label: 'Address', sortable: true, searchable: true }
  ];

  // Form fields component with error support
  const formFields = (fieldErrors: Record<string, string>) => (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Company Code <RequiredIndicator />
        </label>
        <Input
          type="text"
          value={formData.code}
          onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
          required
          placeholder="e.g., ACME001"
        />
        <FieldError error={fieldErrors.code} />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Company Name <RequiredIndicator />
        </label>
        <Input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          required
          placeholder="e.g., Acme Corporation"
        />
        <FieldError error={fieldErrors.name} />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Description
        </label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Enter company description..."
          rows={2}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Address
        </label>
        <Textarea
          value={formData.address}
          onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
          placeholder="Enter company address..."
          rows={2}
        />
      </div>
    </div>
  );

  // Modal icon
  const modalIcon = (
    <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
      <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    </div>
  );

  return (
    <BaseCRUDPage<Company, CompanyCreate, CompanyUpdate>
      title="Company Management"
      entityName="company"
      columns={columns}
      service={companyService}
      formFields={formFields}
      formData={formData}
      setFormData={setFormData}
      requiredFields={['code', 'name']}
      getInitialFormData={() => ({ 
        code: '', 
        name: '', 
        description: '', 
        address: '' 
      })}
      toCreateData={(formData) => ({
        code: formData.code,
        name: formData.name,
        description: formData.description || undefined,
        address: formData.address || undefined
      })}
      toUpdateData={(formData, original) => {
        const updateData: CompanyUpdate = {};
        if (formData.code !== original.code) updateData.code = formData.code;
        if (formData.name !== original.name) updateData.name = formData.name;
        if (formData.description !== (original.description || '')) updateData.description = formData.description || null;
        if (formData.address !== (original.address || '')) updateData.address = formData.address || null;
        return updateData;
      }}
      toFormData={(company) => ({
        code: company.code,
        name: company.name,
        description: company.description || '',
        address: company.address || ''
      })}
      modalIcon={modalIcon}
      modalTitle={(isEdit) => ({
        title: isEdit ? 'Edit Company' : 'Create New Company',
        subtitle: isEdit ? 'Update company information' : 'Add a new company to the system'
      })}
      customDeleteConfirm={(company) => `Are you sure you want to delete company "${company.name}"?`}
      defaultSearchColumn="code"
    />
  );
};

export default CompaniesPage;
